import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, jobId } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { JobDialog } from "@/pages/JobDialog";
import { ClosingReminders, ClosingBadge } from "@/components/site/ClosingReminders";

const Cell = ({ children, className = "" }) => <td className={`p-4 align-top ${className}`}>{children}</td>;

const Table = ({ head, children, testId }) => (
  <div className="overflow-x-auto border border-white/10" data-lenis-prevent>
    <table data-testid={testId} className="w-full min-w-[720px] text-left text-sm">
      <thead className="bg-[#0E2260] font-mono text-[11px] uppercase tracking-[0.14em] text-slate-300"><tr>{head.map((h) => <th key={h} className="p-4">{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [inqs, setInqs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [j, a, q, al] = await Promise.all([api.get("/admin/jobs"), api.get("/admin/applications"), api.get("/admin/inquiries"), api.get("/admin/job-alerts")]);
      setJobs(j.data ?? []); setApps(a.data ?? []); setInqs(q.data ?? []); setAlerts(al.data ?? []);
    } catch (e) {
      toast.error(formatError(e));
    }
  }, []);
  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (user === null) return <p className="p-10 text-slate-300">Checking session…</p>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const toggle = async (j) => {
    const { _id, id, created_at, updated_at, ...rest } = j;
    await api.put(`/admin/jobs/${jobId(j)}`, { ...rest, status: j.status === "open" ? "closed" : "open" });
    toast.success(`Role ${j.status === "open" ? "closed" : "reopened"}`);
    load();
  };
  const remove = async (j) => {
    if (!window.confirm(`Delete "${j.title}"?`)) return;
    await api.delete(`/admin/jobs/${jobId(j)}`);
    load();
  };

  return (
    <div className="min-h-screen bg-[#061235] text-white"><div data-testid="admin-page" className="mx-auto max-w-[1400px] px-5 py-10 md:px-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4A24C]">← Site</Link>
          <h1 className="font-display mt-2 text-5xl font-black uppercase">Admin</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 border border-white/15 px-4 py-2 text-sm"><LogOut className="h-4 w-4" />Sign out</button>
      </div>
      <Tabs defaultValue="jobs" className="mt-8">
        <TabsList className="rounded-none bg-[#0E2260]">
          <TabsTrigger value="jobs" data-testid="tab-jobs" className="rounded-none">Jobs ({jobs.length})</TabsTrigger>
          <TabsTrigger value="apps" data-testid="tab-applications" className="rounded-none">Applications ({apps.length})</TabsTrigger>
          <TabsTrigger value="inq" data-testid="tab-inquiries" className="rounded-none">Inquiries ({inqs.length})</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-job-alerts" className="rounded-none">Job alerts ({alerts.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="jobs" className="mt-6">
          <ClosingReminders jobs={jobs} onEdit={setEditing} />
          <button onClick={() => setEditing({})} data-testid="admin-new-job" className="mb-4 flex items-center gap-2 bg-[#C8102E] px-4 py-3 text-sm font-semibold text-white"><Plus className="h-4 w-4" />New role</button>
          <Table head={["Title", "Department", "Owner", "Valid through", "Open", ""]} testId="admin-jobs-table">
            {jobs.map((j, i) => (
              <tr key={jobId(j)} className="border-t border-white/10" data-testid={`admin-job-row-${i}`}>
                <Cell className="font-semibold">{j.title}</Cell><Cell>{j.department}</Cell><Cell>{j.owner}</Cell><Cell>{j.valid_through || "—"}<ClosingBadge job={j} testId={`admin-job-closing-${i}`} /></Cell>
                <Cell><Switch checked={j.status === "open"} onCheckedChange={() => toggle(j)} data-testid={`admin-job-toggle-${i}`} /></Cell>
                <Cell className="whitespace-nowrap">
                  <button onClick={() => setEditing(j)} data-testid={`admin-job-edit-${i}`} className="mr-3 text-slate-300 hover:text-white"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(j)} data-testid={`admin-job-delete-${i}`} className="text-red-300 hover:text-red-200"><Trash2 className="h-4 w-4" /></button>
                </Cell>
              </tr>
            ))}
          </Table>
        </TabsContent>
        <TabsContent value="apps" className="mt-6">
          <Table head={["Date", "Role", "Name", "Email", "Resume", "Note"]} testId="admin-applications-table">
            {apps.map((a) => <tr key={jobId(a)} className="border-t border-white/10"><Cell className="font-mono text-xs">{a.created_at?.slice(0, 10)}</Cell><Cell>{a.job_title}</Cell><Cell>{a.name}</Cell><Cell>{a.email}</Cell><Cell className="max-w-[200px] break-all">{a.resume_link || "—"}</Cell><Cell className="text-slate-300">{a.message || "—"}</Cell></tr>)}
          </Table>
        </TabsContent>
        <TabsContent value="inq" className="mt-6">
          <Table head={["Ref", "Date", "Audience", "Routed to", "From", "Message"]} testId="admin-inquiries-table">
            {inqs.map((q) => <tr key={jobId(q)} className="border-t border-white/10"><Cell className="font-mono text-xs">{q.reference}</Cell><Cell className="font-mono text-xs">{q.created_at?.slice(0, 10)}</Cell><Cell>{q.audience}</Cell><Cell>{q.routed_to}</Cell><Cell>{q.name}<br /><span className="text-slate-400">{q.email}</span></Cell><Cell className="text-slate-300">{q.message}</Cell></tr>)}
          </Table>
        </TabsContent>
        <TabsContent value="alerts" className="mt-6">
          <Table head={["Date", "Name", "Email", "Interest"]} testId="admin-job-alerts-table">
            {alerts.map((s) => <tr key={jobId(s)} className="border-t border-white/10"><Cell className="font-mono text-xs">{s.created_at?.slice(0, 10)}</Cell><Cell>{s.name || "—"}</Cell><Cell>{s.email}</Cell><Cell>{s.interest}</Cell></tr>)}
          </Table>
        </TabsContent>
      </Tabs>
      <JobDialog job={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div></div>
  );
}
